import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  SimpleChanges,
} from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { CHART_TYPES } from '@app-reports/constants/reports.constants';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { ReportDomain, CrossDomainPlan } from '@app-reports/models/reports.model';
import { EChartsOption } from 'echarts';
import * as echarts from 'echarts';

@Component({
  selector: 'app-talk-to-data-chart-view',
  templateUrl: './talk-to-data-chart-view.component.html',
  styleUrls: ['./talk-to-data-chart-view.component.scss'],
})
export class TalkToDataChartViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() isDark: boolean = false;
  @Input() tableQueryIndex: number = 0;
  @Input() dataSource: any[] = [];
  @Input() displayedColumns: string[] = [];
  @Input() reportPeriods: any[] = [];
  @Input() selectedPeriod: string = 'Past 7 days';
  @Input() entityType: string[] = ['asset', 'driver'];
  @Input() userTags: any[] = [];
  @Input() unSelectAllTag: boolean = false;
  @Input() csvLoader: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() showNoDataMessage: boolean = true;
  @Input() conversationState: string = 'initial';
  @Input() sessionId: string = '';
  @Input() domain: ReportDomain = 'trip';
  @Input() sqlStatements: string[] = [];
  @Input() crossDomainPlan: CrossDomainPlan | null = null;
  @Input() saveReportLoader: boolean = false;
  @Input() actionControlsDisabled: boolean = false;
  @Input() isWorkflowAwaitingInput: boolean = false;
  @Input() set isActiveTab(value: boolean) {
    this._isActiveTab = value;
    if (value && this.hasEnoughDataForChart && !this.chartInstance) {
      setTimeout(() => this.generateChart(), 50);
    } else if (value && this.chartInstance) {
      setTimeout(() => this.chartInstance?.resize(), 50);
    }
  }
  get isActiveTab(): boolean {
    return this._isActiveTab;
  }
  private _isActiveTab: boolean = false;

  public chartTypes = CHART_TYPES;

  @Output() periodChange = new EventEmitter<string>();
  @Output() tagsSelected = new EventEmitter<any>();
  @Output() tagDropdownClosed = new EventEmitter<void>();
  @Output() downloadReport = new EventEmitter<void>();
  @Output() saveReport = new EventEmitter<void>();

  @ViewChild('chartCanvas', { static: false }) chartCanvas!: ElementRef;

  filterForm!: FormGroup;
  chartOptions!: EChartsOption;
  chartInstance: any;
  selectedChartType: string = 'bar';
  chartLoading: boolean = false;
  private chartOptionsCache: Map<string, EChartsOption> = new Map();

  private resizeHandler = () => {
    if (this.chartInstance) {
      this.chartInstance.resize();
    }
  };
  private mutationObserver: MutationObserver | null = null;

  constructor(private fb: FormBuilder, private reportService: ReportsService) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conversationState'] && changes['conversationState'].currentValue === 'initial') {
      this.selectedChartType = 'bar';
      if (this.filterForm) {
        this.filterForm.patchValue({ chartType: 'bar' });
      }
    }
    if (changes['dataSource']) {
      this.chartOptionsCache.clear();
      if (this.hasEnoughDataForChart) {
        if (this._isActiveTab) {
          this.generateChart();
        }
      } else {
        if (this.chartInstance) {
          this.chartInstance.dispose();
          this.chartInstance = null;
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (this._isActiveTab && this.hasEnoughDataForChart) {
      setTimeout(() => this.generateChart(), 100);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.resizeHandler);
    this.mutationObserver?.disconnect();
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
    this.chartOptionsCache.clear();
  }

  initializeForm(): void {
    this.filterForm = this.fb.group({
      period: [this.selectedPeriod],
      tags: [[]],
      chartType: [this.selectedChartType],
    });
  }
  get hasEnoughDataForChart(): boolean {
    return this.dataSource?.length > 2;
  }

  getSelectedChartIcon(): string {
    return this.chartTypes.find((t) => t.value === this.selectedChartType)?.icon ?? 'bar_chart';
  }

  getSelectedChartLabel(): string {
    return this.chartTypes.find((t) => t.value === this.selectedChartType)?.label ?? '';
  }

  generateChart(): void {
    if (!this.dataSource?.length) {
      console.warn('No data available to generate chart');
      return;
    }
    if (this.chartOptionsCache.has(this.selectedChartType)) {
      this.chartOptions = this.chartOptionsCache.get(this.selectedChartType)!;
      setTimeout(() => this.renderChart(), 100);
      return;
    }

    this.chartLoading = true;

    const requestBody = this.prepareChartRequestBody();
    const isRefresh = false;

    this.reportService.getGenerateVisuals(requestBody, isRefresh).subscribe({
      next: (response: any) => {
        const answerRaw = response?.answer;

        if (!answerRaw) {
          console.warn('No answer in chart response');
          this.chartLoading = false;
          return;
        }

        try {
          const chartData: any = typeof answerRaw === 'string' ? JSON.parse(answerRaw) : answerRaw;
          const builtOptions = this.buildEChartsOptions(chartData);
          this.chartOptionsCache.set(this.selectedChartType, builtOptions);
          this.chartOptions = builtOptions;
          setTimeout(() => this.renderChart(), 100);
        } catch (e) {
          console.error('Failed to parse chart answer:', e);
        }

        this.chartLoading = false;
      },
      error: (err) => {
        console.error('Chart API error:', err);
        this.chartLoading = false;
      },
    });
  }

  prepareChartRequestBody(): any {
    const normalizedSql = Array.isArray(this.sqlStatements)
      ? this.sqlStatements.filter((sql) => typeof sql === 'string' && sql.trim().length > 0)
      : [];

    const tqi = this.tableQueryIndex ?? 0;
    return {
      sessionId: this.sessionId || undefined,
      chartType: this.selectedChartType,
      data: this.dataSource,
      domain: this.domain || 'trip',
      sql: (this.domain || 'trip') === 'cross_domain' ? normalizedSql : normalizedSql[tqi] ?? normalizedSql[0] ?? '',
      tableQueryIndex: tqi,
      ...(this.crossDomainPlan ? { crossDomainPlan: this.crossDomainPlan } : {}),
    };
  }
  private isDatetimeLabel(labels: any[]): boolean {
    if (!labels?.length) return false;
    return /^\d{4}-\d{2}-\d{2}[\sT]\d{2}:\d{2}/.test(String(labels[0]));
  }

  buildEChartsOptions(data: any): EChartsOption {
    const isPieChart = data.series?.length > 0 && (data.series[0]?.type === 'pie' || data.series[0]?.type === 'donut');

    const xLabels: any[] = data.xAxis?.data ?? [];
    const labelCount = xLabels.length;
    const isDatetime = this.isDatetimeLabel(xLabels);
    const isAreaChart = (data.series ?? []).some((s: any) => s.type === 'area');
    const isScatterChart = (data.series ?? []).some((s: any) => s.type === 'scatter');
    const isBarChart = (data.series ?? []).some((s: any) => s.type === 'bar');
    const shouldRotate = isDatetime || (labelCount > 5 && !(isAreaChart || isScatterChart || isBarChart));
    const labelRotation = isAreaChart || isScatterChart || isBarChart ? 0 : shouldRotate ? 45 : 0;
    const nameGap = isAreaChart || isScatterChart || isBarChart ? 35 : isDatetime ? 90 : shouldRotate ? 75 : 35;
    const hasTitle = !!data.title?.text;
    const hasSubtitle = !!data.title?.subtext;
    const gridTop = hasTitle ? (hasSubtitle ? 65 : 50) : 20;
    let gridLeft = data.grid?.left ?? '8%';
    if (xLabels && xLabels.length > 0) {
      const maxLabelLength = Math.max(...xLabels.map((l: any) => String(l).length));
      if (maxLabelLength > 12) {
        gridLeft = '14%';
      } else if (maxLabelLength > 8) {
        gridLeft = '10%';
      }
    }
    const gridBottom = isDatetime ? '28%' : shouldRotate ? '22%' : '14%';

    const baseOptions: EChartsOption = {
      title: undefined,
      tooltip: data.tooltip ?? {
        trigger: isPieChart ? 'item' : 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(50, 50, 50, 0.9)',
        borderColor: '#333',
        borderWidth: 1,
        textStyle: { color: '#fff', fontSize: 12 },
      },
      animationEasing: data.animationEasing ?? 'elasticOut',
      series: [],
    };

    if (isPieChart) {
      const sliceCount = data.series?.[0]?.data?.length ?? 0;
      const useOutsideLabels = sliceCount <= 6;

      baseOptions.series = (data.series ?? []).map((s: any) => {
        if (s.type === 'pie' && (!s.radius || s.radius[0] === '0%' || s.radius[0] === 0)) {
          return {
            ...s,
            radius: useOutsideLabels ? ['0%', '60%'] : '60%',
            center: useOutsideLabels ? ['50%', '55%'] : ['42%', '55%'],
            label: useOutsideLabels ? { show: true, position: 'outside', fontSize: 11, formatter: '{b}: {d}%' } : { show: false },
            labelLine: useOutsideLabels ? { show: true, smooth: true, length: 12, length2: 18, minTurnAngle: 90 } : { show: false },
            emphasis: {
              label: { show: true, fontSize: 13, fontWeight: 'bold', formatter: '{b}\n{d}%' },
            },
          };
        } else if (s.type === 'donut' || (s.type === 'pie' && s.radius && s.radius[0] !== '0%')) {
          return {
            ...s,
            type: 'pie',
            radius: useOutsideLabels ? ['40%', '60%'] : ['45%', '60%'],
            center: useOutsideLabels ? ['50%', '55%'] : ['42%', '55%'],
            label: useOutsideLabels ? { show: true, position: 'outside', fontSize: 11, formatter: '{b}: {d}%' } : { show: false },
            labelLine: useOutsideLabels ? { show: true, smooth: true, length: 12, length2: 18, minTurnAngle: 90 } : { show: false },
            emphasis: {
              label: { show: true, fontSize: 13, fontWeight: 'bold', formatter: '{b}\n{d}%' },
            },
          };
        }
        return s;
      });

      if (!useOutsideLabels) {
        (baseOptions as any).legend = {
          type: 'scroll',
          orient: 'vertical',
          right: '2%',
          top: 'middle',
          itemWidth: 10,
          itemHeight: 10,
          textStyle: { fontSize: 11 },
        };
      }
    } else {
      const palette = ['#8b2682', '#268b7a', '#e57373', '#64b5f6', '#ffd54f', '#81c784'];

      baseOptions.series = (data.series ?? []).map((s: any, idx: number) => {
        if (s.type === 'area') {
          const areaColor = (s.lineStyle && s.lineStyle.color) || (s.itemStyle && s.itemStyle.color) || palette[idx % palette.length];
          // Area chart: strong fill, lighter line
          return {
            ...s,
            type: 'line',
            lineStyle: {
              ...(s.lineStyle || {}),
              width: 1.5,
              color: areaColor + '99', // lighter line
              z: 2,
            },
            areaStyle: {
              color: {
                type: 'linear',
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: areaColor + 'E6' }, // strong fill
                  { offset: 1, color: areaColor + '00' },
                ],
              },
              opacity: 0.7,
            },
            symbol: s.symbol || 'circle',
            symbolSize: s.symbolSize || 6,
          };
        } else if (s.type === 'line') {
          const { areaStyle, ...rest } = s;

          return {
            ...rest,
            type: 'line',
            areaStyle: undefined,
            lineStyle: {
              ...(s.lineStyle || {}),
              width: 3,
              z: 2,
            },
            symbol: s.symbol || 'circle',
            symbolSize: s.symbolSize || 6,
          };
        }

        return s;
      });

      (baseOptions as any).grid = {
        ...(data.grid ?? {}),
        left: gridLeft,
        right: data.grid?.right ?? (shouldRotate ? '6%' : '4%'),
        top: gridTop,
        bottom: data.grid?.bottom ?? gridBottom,
        containLabel: true,
      };

      if (data.xAxis) {
        const axisLabelFormatter = isDatetime ? (value: string) => String(value).replace(/:\d{2}$/, '') : undefined;

        (baseOptions as any).xAxis = {
          ...data.xAxis,
          nameLocation: 'middle',
          nameGap: nameGap,
          nameTextStyle: { fontSize: 14, fontWeight: 500, color: '#212121' },
          axisLabel: {
            fontSize: 11,
            color: '#555',
            interval: 0,
            rotate: labelRotation,
            overflow: 'truncate',
            width: isDatetime ? 110 : shouldRotate ? 90 : Math.max(40, Math.floor(500 / Math.max(labelCount, 1))),
            ...(axisLabelFormatter ? { formatter: axisLabelFormatter } : {}),
          },
          axisLine: { lineStyle: { color: '#ddd' } },
        };
      }

      if (data.yAxis) {
        (baseOptions as any).yAxis = {
          ...data.yAxis,
          nameTextStyle: { fontSize: 14, color: '#212121', align: 'center' },
          nameLocation: 'middle',
          nameRotate: 90,
          nameGap: 35,
          axisLabel: {
            fontSize: 11,
            color: '#555',
            formatter: (value: number) => (value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toString()),
          },
          splitLine: { lineStyle: { color: '#f0f0f0', type: 'dashed' } },
        };
      }
    }

    return baseOptions;
  }

  renderChart(): void {
    if (!this.chartCanvas?.nativeElement || !this.chartOptions) {
      return;
    }

    this.chartCanvas.nativeElement.style.minHeight = '320px';
    this.chartCanvas.nativeElement.style.minWidth = '100%';

    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }

    try {
      this.chartInstance = echarts.init(this.chartCanvas.nativeElement);

      if (this.chartOptions) {
        this.chartInstance.setOption(this.chartOptions, true);
      }

      window.removeEventListener('resize', this.resizeHandler);
      window.addEventListener('resize', this.resizeHandler);
    } catch (error) {
      console.error('Error rendering chart:', error);
    }
  }

  selectChartType(type: string): void {
    this.selectedChartType = type;
    this.filterForm.patchValue({ chartType: type });
    this.generateChart();
  }

  onPeriodChange(period: string): void {
    this.selectedPeriod = period;
    this.filterForm.patchValue({ period });
    this.periodChange.emit(period);
  }

  onTagsChange(tags: any[]): void {
    this.filterForm.patchValue({ tags });
    this.tagsSelected.emit(tags);
  }

  onTagDropdownClosedEvent(): void {
    this.tagDropdownClosed.emit();
  }

  onDownloadGraph(): void {
    if (!this.chartInstance) return;
    this.chartInstance.resize();
    const url = this.chartInstance.getDataURL({
      pixelRatio: 2,
      backgroundColor: '#fff',
    });

    const link = document.createElement('a');
    link.download = `chart-${this.sessionId || 'export'}.png`;
    link.href = url;
    link.click();
  }

  onSaveReport(): void {
    this.saveReport.emit();
  }
}
